import { create } from "@bufbuild/protobuf";
import {
  CompositionStatus,
  MessageSchema,
  TextContentSchema,
} from "@generated/proto/model";

import type { Message } from '@generated/proto/model'

interface MessageRow {
  id: string
  proto: Message
  created?: Date
  modified?: Date
}

interface StreamingMessage {
  messageId: string
  content: string
  isComplete: boolean
  created: Date
}

interface MergeResult {
  messages: MessageRow[]
  toCleanup: string[]
}

/**
 * Pure function to merge Firestore messages with streaming messages.
 * This function determines which messages should be displayed and which streaming
 * messages should be cleaned up, without any side effects.
 */
export function mergeStreamingWithFirestore(
  firestoreMessages: MessageRow[],
  streamingMessages: Map<string, StreamingMessage>
): MergeResult {
  // Early return if no streaming messages, but still apply filtering
  if (streamingMessages.size === 0) {
    return {
      messages: filterOutUnnecessaryPlaceholders(firestoreMessages),
      toCleanup: []
    }
  }

  const messageMap = new Map<string, MessageRow>()
  const toCleanup: string[] = []

  // Start with all Firestore messages
  firestoreMessages.forEach(msg => {
    messageMap.set(msg.proto.messageId, msg)
  })

  // Process each streaming message
  streamingMessages.forEach((streamingMsg, messageId) => {
    const existingMsg = messageMap.get(messageId)

    if (!existingMsg) {
      // No Firestore message found - this is unusual but not an error
      return
    }

    const mergeDecision = decideMergeStrategy(existingMsg, streamingMsg)

    switch (mergeDecision.action) {
      case 'use-streaming':
        messageMap.set(messageId, createStreamingMessage(existingMsg, streamingMsg))
        break
      case 'use-firestore':
        // Message stays as-is from Firestore, schedule streaming cleanup
        toCleanup.push(messageId)
        break
      case 'cleanup-completed':
        // Empty completed streaming should be cleaned up immediately
        toCleanup.push(messageId)
        break
    }
  })

  // Convert back to array and sort by creation time
  let messages = Array.from(messageMap.values()).sort((a, b) => {
    const aTime = a.created || new Date(0)
    const bTime = b.created || new Date(0)
    return aTime.getTime() - bTime.getTime()
  })

  // Filter out empty placeholders that appear before tool calls/responses
  messages = filterOutUnnecessaryPlaceholders(messages)

  return { messages, toCleanup }
}

type MergeAction = 'use-streaming' | 'use-firestore' | 'cleanup-completed'

/**
 * Filters out unnecessary empty placeholders that appear before tool calls/responses.
 * Only keeps the most recent placeholder if it's the last message in the list.
 */
function filterOutUnnecessaryPlaceholders(messages: MessageRow[]): MessageRow[] {
  const filtered: MessageRow[] = []
  
  for (let i = 0; i < messages.length; i++) {
    const current = messages[i]
    const next = messages[i + 1]
    const isLast = i === messages.length - 1
    
    // Check if current message is an empty placeholder
    const isEmptyPlaceholder = isMessageEmptyPlaceholder(current)
    const nextIsToolCall = next ? isToolCallOrResponse(next) : false
    
    if (isEmptyPlaceholder) {
      // If it's the last message, keep it (might be actively streaming)
      if (isLast) {
        filtered.push(current)
      }
      // If next message is a tool call or tool response, skip this placeholder
      else if (next && nextIsToolCall) {
        continue // Skip this placeholder
      }
      // Otherwise keep the placeholder
      else {
        filtered.push(current)
      }
    } else {
      // Not a placeholder, always keep
      filtered.push(current)
    }
  }
  
  return filtered
}

/**
 * Checks if a message is an empty placeholder that should be filtered out
 */
function isMessageEmptyPlaceholder(message: MessageRow): boolean {
  const content = message.proto.content
  
  // Text content that's empty (includes streaming placeholders)
  if (content?.case === 'textContent' && content.value) {
    const textContent = content.value.content || ''
    const compositionStatus = content.value.compositionStatus
    const isStreaming = compositionStatus === CompositionStatus.AGENT_STREAMING
    const isComplete = compositionStatus === CompositionStatus.COMPLETE
    const isEmpty = textContent.trim() === ''
    
    // An empty placeholder is:
    // 1. Empty text content that is marked as streaming (old streaming placeholder saved to Firestore)
    // 2. Empty text content that is marked as complete (empty completed message)
    return isEmpty && (isStreaming || isComplete)
  }
  
  // No content at all
  if (!content?.case) {
    return true
  }
  
  return false
}

/**
 * Checks if a message is a tool call or tool response
 */
function isToolCallOrResponse(message: MessageRow): boolean {
  const content = message.proto.content
  return content?.case === 'toolCall' || content?.case === 'toolResponse'
}

interface MergeDecision {
  action: MergeAction
  reason: string
}

/**
 * Determines what action to take when merging a streaming message
 * with its Firestore counterpart
 */
function decideMergeStrategy(
  firestoreMsg: MessageRow,
  streamingMsg: StreamingMessage
): MergeDecision {
  const content = firestoreMsg.proto.content
  const contentCase = content?.case
  const value = content?.value

  const isFirestoreStreaming = contentCase === 'textContent'
    && value?.compositionStatus === CompositionStatus.AGENT_STREAMING
  const isFirestoreComplete = contentCase === 'textContent'
    && value?.compositionStatus === CompositionStatus.COMPLETE
  const firestoreContent = contentCase === 'textContent' ? value?.content || '' : ''

  // Detect if this might be a tool call message (no text content expected)
  const isLikelyToolCall = streamingMsg.content.length === 0 &&
    (contentCase !== 'textContent' || (contentCase === 'textContent' && value?.content === ''))

  // ALWAYS prefer Firestore if it has complete content - this ensures smooth transition
  if (isFirestoreComplete && firestoreContent.trim() !== '') {
    return {
      action: 'use-firestore',
      reason: `Firestore complete with content (${firestoreContent.length} chars) - prefer over streaming`
    }
  }

  // If streaming is active and Firestore doesn't have final content, show streaming
  if (!streamingMsg.isComplete && (isFirestoreStreaming || firestoreContent.trim() === '')) {
    return {
      action: 'use-streaming',
      reason: isLikelyToolCall
        ? 'Tool call placeholder - showing streaming state'
        : `Streaming in progress (${streamingMsg.content.length} chars)`
    }
  }

  // If streaming is complete but empty (like tool calls), clean it up
  if (streamingMsg.isComplete && streamingMsg.content.length === 0) {
    return {
      action: 'cleanup-completed',
      reason: isLikelyToolCall
        ? 'Tool call streaming completed - cleanup immediately'
        : 'Empty streaming completed - cleanup'
    }
  }

  // If streaming is complete and has content, but Firestore doesn't, keep streaming visible
  if (streamingMsg.isComplete && streamingMsg.content.trim() !== '' && firestoreContent.trim() === '') {
    return {
      action: 'use-streaming',
      reason: 'Streaming complete with content but Firestore not ready - keep streaming visible'
    }
  }

  // Default: keep as-is (Firestore)
  return {
    action: 'use-firestore',
    reason: 'Default - no streaming action needed'
  }
}

/**
 * Creates a message row with streaming content overlaid on the Firestore message structure
 */
function createStreamingMessage(
  firestoreMsg: MessageRow,
  streamingMsg: StreamingMessage
): MessageRow {
  return {
    ...firestoreMsg,
    proto: create(MessageSchema, {
      ...firestoreMsg.proto,
      content: {
        case: 'textContent',
        value: create(TextContentSchema, {
          content: streamingMsg.content,
          compositionStatus: CompositionStatus.AGENT_STREAMING
        })
      }
    })
  }
}

/**
 * Helper function to get debug information about merge decisions
 * Useful for troubleshooting merge behavior
 */
export function getMergeDebugInfo(
  firestoreMessages: MessageRow[],
  streamingMessages: Map<string, StreamingMessage>
): Record<string, any> {
  const debugInfo: Record<string, any> = {}

  streamingMessages.forEach((streamingMsg, messageId) => {
    const firestoreMsg = firestoreMessages.find(m => m.proto.messageId === messageId)
    if (firestoreMsg) {
      const decision = decideMergeStrategy(firestoreMsg, streamingMsg)
      debugInfo[messageId] = {
        streaming: {
          content: streamingMsg.content.substring(0, 50),
          isComplete: streamingMsg.isComplete,
          contentLength: streamingMsg.content.length
        },
        firestore: {
          contentCase: firestoreMsg.proto.content?.case,
          compositionStatus: firestoreMsg.proto.content?.value?.compositionStatus,
          contentLength: firestoreMsg.proto.content?.value?.content?.length || 0
        },
        decision: decision.action,
        reason: decision.reason
      }
    }
  })

  return debugInfo
}